import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router";

// ─── Layout (non-lazy: renders immediately) ───────────────────────────────────
import Root from "./pages/Root";
import AdminGuard from "./components/AdminGuard";

// ─── Public Pages (lazy-loaded) ───────────────────────────────────────────────
const Home = lazy(() => import("./pages/Home"));
const Properties = lazy(() => import("./pages/Properties"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));

// ─── Admin Pages (lazy-loaded, separate chunk) ────────────────────────────────
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminProjects = lazy(() => import("./pages/admin/AdminProjects"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminSiteVisits = lazy(() => import("./pages/admin/AdminSiteVisits"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminSellers = lazy(() => import("./pages/admin/AdminSellers"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminProperties = lazy(() => import("./pages/admin/AdminProperties"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));

// ─── Page Loading Fallback ─────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div
      role="status"
      aria-label="Loading page"
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          border: '3px solid #e5e7eb',
          borderTopColor: 'var(--brand-pink)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function withSuspense(Component: React.ComponentType) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    Component: Root,
    children: [
      { index: true, element: withSuspense(Home) },
      { path: "properties", element: withSuspense(Properties) },
      { path: "properties/:id", element: withSuspense(PropertyDetail) },
      { path: "contact", element: withSuspense(Contact) },
      { path: "blog", element: withSuspense(Blog) },
      { path: "blog/:slug", element: withSuspense(BlogPost) },
    ],
  },
  {
    path: "/admin-login",
    element: withSuspense(AdminLogin)
  },
  {
    path: "/admin-dashboard",
    element: (
      <AdminGuard>
        <Suspense fallback={<PageLoader />}>
          <AdminLayout />
        </Suspense>
      </AdminGuard>
    ),
    children: [
      { index: true, element: withSuspense(Dashboard) },
      { path: "properties", element: withSuspense(AdminProjects) },
      { path: "leads", element: withSuspense(AdminLeads) },
      { path: "site-visits", element: withSuspense(AdminSiteVisits) },
      { path: "analytics", element: withSuspense(AdminAnalytics) },
      { path: "sellers", element: withSuspense(AdminSellers) },
      { path: "sell-properties", element: withSuspense(AdminProperties) },
      { path: "blog", element: withSuspense(AdminBlog) },
      { path: "settings", element: withSuspense(AdminSettings) },
    ],
  },
]);



